import { Head, router } from "@inertiajs/react"
import type { FormDataConvertible } from "@inertiajs/core"
import {
  ArrowLeft,
  Globe,
  Eye,
  ScanLine,
  Sparkles,
  Loader2,
  Save,
  ExternalLink,
  QrCode,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import AppLayout from "@/layouts/app-layout"
import {
  coffeesPath,
  editCoffeePath,
  coffeePath,
  publishCoffeePath,
  unpublishCoffeePath,
  generateAiContentCoffeePath,
  generateRecipesCoffeePath,
  qrCoffeePath,
} from "@/routes"
import type {
  BreadcrumbItem,
  PublishedCoffeeFull,
  VisibilityToggles,
} from "@/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import { ScoreRadarChart } from "@/components/charts/ScoreRadarChart"

interface Props {
  coffee: PublishedCoffeeFull
  can_publish: boolean
}

const defaultToggles: VisibilityToggles = {
  show_scores: true,
  show_origin: true,
  show_recipes: true,
  show_flavor_wheel: true,
  show_tasting_notes: true,
  show_producer: true,
}

export default function CoffeesEdit({ coffee, can_publish }: Props) {
  const { t } = useTranslation()
  const toggles = { ...defaultToggles, ...(coffee.visibility_toggles || {}) }
  const custom = coffee.custom_content || {
    tagline: "",
    story: "",
    altitude: "",
    harvest: "",
  }

  const [name, setName] = useState(coffee.name)
  const [tagline, setTagline] = useState(custom.tagline || "")
  const [story, setStory] = useState(custom.story || "")
  const [altitude, setAltitude] = useState(custom.altitude || "")
  const [harvest, setHarvest] = useState(custom.harvest || "")
  const [visibilityToggles, setVisibilityToggles] =
    useState<VisibilityToggles>(toggles)
  const [consumerNotes, setConsumerNotes] = useState(
    coffee.ai_content?.consumer_notes || "",
  )
  const [technicalNotes, setTechnicalNotes] = useState(
    coffee.ai_content?.technical_notes || "",
  )
  const [description, setDescription] = useState(
    coffee.ai_content?.description || "",
  )
  const [flavorTags, setFlavorTags] = useState(
    coffee.ai_content?.flavor_tags || [],
  )
  const [generatingAI, setGeneratingAI] = useState(false)
  const [generatingRecipes, setGeneratingRecipes] = useState(false)
  const [saving, setSaving] = useState(false)

  const breadcrumbs: BreadcrumbItem[] = [
    { title: t("coffees.breadcrumb", "My Coffees"), href: coffeesPath() },
    { title: coffee.name, href: editCoffeePath(coffee.id) },
  ]

  const protocol = coffee.cupping_form?.includes("cva") ? "cva" : "sca"
  const sample = coffee.pinned_sample
  const scores = coffee.pinned_scores

  const handleSave = () => {
    setSaving(true)
    const data = {
      coffee: {
        name,
        visibility_toggles: { ...visibilityToggles },
        custom_content: { tagline, story, altitude, harvest },
        ai_content: {
          consumer_notes: consumerNotes,
          technical_notes: technicalNotes,
          description,
          flavor_tags: flavorTags,
        },
      },
    }
    router.put(
      coffeePath(coffee.id),
      data as unknown as Record<string, FormDataConvertible>,
      { onFinish: () => setSaving(false) },
    )
  }

  const handlePublish = () => {
    router.post(publishCoffeePath(coffee.id))
  }

  const handleUnpublish = () => {
    router.post(unpublishCoffeePath(coffee.id))
  }

  const handleGenerateAI = async () => {
    setGeneratingAI(true)
    try {
      const response = await fetch(generateAiContentCoffeePath(coffee.id), {
        method: "POST",
        headers: {
          "X-CSRF-Token":
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content") || "",
          "Content-Type": "application/json",
        },
      })
      const data = await response.json()
      if (data.error) throw new Error(data.error)
      setConsumerNotes(data.consumer_notes || "")
      setTechnicalNotes(data.technical_notes || "")
      setDescription(data.description || "")
      setFlavorTags(data.flavor_tags || [])
    } catch (err) {
      console.error("AI generation failed:", err)
    } finally {
      setGeneratingAI(false)
    }
  }

  const handleGenerateRecipes = async () => {
    setGeneratingRecipes(true)
    try {
      const response = await fetch(generateRecipesCoffeePath(coffee.id), {
        method: "POST",
        headers: {
          "X-CSRF-Token":
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content") || "",
          "Content-Type": "application/json",
        },
      })
      const data = await response.json()
      if (data.error) throw new Error(data.error)
      router.reload({ only: ["coffee"] })
    } catch (err) {
      console.error("Recipe generation failed:", err)
    } finally {
      setGeneratingRecipes(false)
    }
  }

  const toggleVisibility = (key: keyof VisibilityToggles) => {
    setVisibilityToggles((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${t("common.edit", "Edit")} - ${coffee.name}`} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => router.visit(coffeesPath())}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{coffee.name}</h1>
                <Badge
                  className={
                    coffee.status === "published"
                      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                      : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                  }
                  variant="outline"
                >
                  {t(`coffees.status_${coffee.status}`, coffee.status)}
                </Badge>
              </div>
              <div className="text-muted-foreground mt-0.5 flex items-center gap-3 text-sm">
                <span className="flex items-center gap-1">
                  <Eye className="h-3.5 w-3.5" /> {coffee.views_count}{" "}
                  {t("coffees.views", "views")}
                </span>
                <span className="flex items-center gap-1">
                  <ScanLine className="h-3.5 w-3.5" /> {coffee.qr_scans_count}{" "}
                  {t("coffees.scans", "scans")}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => router.visit(qrCoffeePath(coffee.id))}
            >
              <QrCode className="mr-2 h-4 w-4" />
              {t("coffees.qr_code", "QR Code")}
            </Button>
            {coffee.status === "published" && (
              <a
                href={coffee.public_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {t("coffees.view_public", "View Public Page")}
                </Button>
              </a>
            )}
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {t("common.save", "Save")}
            </Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content - 2 columns */}
          <div className="space-y-6 lg:col-span-2">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("coffees.section_info", "Coffee Info")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="name">
                    {t("coffees.name_label", "Name")}
                  </Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="tagline">
                    {t("coffees.tagline_label", "Tagline")}
                  </Label>
                  <Input
                    id="tagline"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder={t(
                      "coffees.tagline_placeholder",
                      "A short, catchy line for the hero section",
                    )}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="story">
                    {t("coffees.story_label", "Story")}
                  </Label>
                  <Textarea
                    id="story"
                    value={story}
                    onChange={(e) => setStory(e.target.value)}
                    placeholder={t(
                      "coffees.story_placeholder",
                      "Tell the story behind this coffee...",
                    )}
                    className="mt-1.5"
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="altitude">
                      {t("coffees.altitude_label", "Altitude")}
                    </Label>
                    <Input
                      id="altitude"
                      value={altitude}
                      onChange={(e) => setAltitude(e.target.value)}
                      placeholder="1,800 - 2,100 masl"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="harvest">
                      {t("coffees.harvest_label", "Harvest")}
                    </Label>
                    <Input
                      id="harvest"
                      value={harvest}
                      onChange={(e) => setHarvest(e.target.value)}
                      placeholder="2025"
                      className="mt-1.5"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tasting Notes */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">
                    {t("coffees.section_notes", "Tasting Notes")}
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateAI}
                    disabled={generatingAI || !scores}
                  >
                    {generatingAI ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="mr-2 h-4 w-4" />
                    )}
                    {t("coffees.generate_ai", "Generate with AI")}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="description">
                    {t("coffees.description_label", "Product Description")}
                  </Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t(
                      "coffees.description_placeholder",
                      "A compelling description for the hero section...",
                    )}
                    className="mt-1.5"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="consumer_notes">
                    {t("coffees.consumer_notes_label", "Consumer Notes")}
                  </Label>
                  <Textarea
                    id="consumer_notes"
                    value={consumerNotes}
                    onChange={(e) => setConsumerNotes(e.target.value)}
                    placeholder={t(
                      "coffees.consumer_notes_placeholder",
                      "Tasting notes for consumers...",
                    )}
                    className="mt-1.5"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="technical_notes">
                    {t("coffees.technical_notes_label", "Technical Notes")}
                  </Label>
                  <Textarea
                    id="technical_notes"
                    value={technicalNotes}
                    onChange={(e) => setTechnicalNotes(e.target.value)}
                    placeholder={t(
                      "coffees.technical_notes_placeholder",
                      "Professional cupper notes...",
                    )}
                    className="mt-1.5"
                    rows={2}
                  />
                </div>
                {flavorTags.length > 0 && (
                  <div>
                    <Label>
                      {t("coffees.flavor_tags_label", "Flavor Tags")}
                    </Label>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      {flavorTags.map((tag, i) => (
                        <Badge key={i} variant="secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Brew Recipes */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">
                    {t("coffees.section_recipes", "Brew Recipes")}
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateRecipes}
                    disabled={generatingRecipes || !scores}
                  >
                    {generatingRecipes ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="mr-2 h-4 w-4" />
                    )}
                    {t("coffees.generate_recipes", "Generate Recipes")}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {coffee.recipes.length === 0 ? (
                  <p className="text-muted-foreground py-4 text-center text-sm">
                    {t(
                      "coffees.no_recipes",
                      "No recipes yet. Generate AI-powered brew recipes based on the sensory profile.",
                    )}
                  </p>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {coffee.recipes.map((recipe) => (
                      <div key={recipe.id} className="rounded-lg border p-3">
                        <p className="mb-1 text-sm font-semibold capitalize">
                          {recipe.method.replace(/_/g, " ")}
                        </p>
                        <div className="text-muted-foreground space-y-0.5 text-xs">
                          {recipe.dose && (
                            <p>
                              {t("coffees.recipe_dose", "Dose")}: {recipe.dose}g
                            </p>
                          )}
                          {recipe.ratio && (
                            <p>
                              {t("coffees.recipe_ratio", "Ratio")}:{" "}
                              {recipe.ratio}
                            </p>
                          )}
                          {recipe.temp && (
                            <p>
                              {t("coffees.recipe_temp", "Temp")}: {recipe.temp}
                              °C
                            </p>
                          )}
                          {recipe.time && (
                            <p>
                              {t("coffees.recipe_time", "Time")}: {recipe.time}
                            </p>
                          )}
                          {recipe.grind && (
                            <p>
                              {t("coffees.recipe_grind", "Grind")}:{" "}
                              {recipe.grind}
                            </p>
                          )}
                        </div>
                        {recipe.ai_tip && (
                          <p className="text-muted-foreground mt-2 border-t pt-2 text-xs italic">
                            {recipe.ai_tip}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-6">
            {/* Publish */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("coffees.section_publish", "Publish")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {coffee.status === "published" ? (
                  <>
                    <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                      <Globe className="h-4 w-4" />
                      {t("coffees.live_at", "Live at")}{" "}
                      <code className="text-xs">/c/{coffee.slug}</code>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleUnpublish}
                      className="w-full"
                    >
                      {t("coffees.unpublish", "Unpublish")}
                    </Button>
                  </>
                ) : (
                  <>
                    {!can_publish && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        {t(
                          "coffees.publish_limit",
                          "You've reached the free tier limit. Upgrade to Pro for unlimited pages.",
                        )}
                      </p>
                    )}
                    <Button
                      onClick={handlePublish}
                      disabled={!can_publish}
                      className="w-full"
                    >
                      <Globe className="mr-2 h-4 w-4" />
                      {t("coffees.publish", "Publish")}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Sensory Profile */}
            {scores && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    {t("coffees.section_sensory", "Sensory Profile")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-2 text-center">
                    <span className="text-3xl font-bold">
                      {scores.total_score}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      {" "}
                      / 100
                    </span>
                  </div>
                  <div className="h-[220px] w-full">
                    <ScoreRadarChart
                      protocol={protocol}
                      scores={{
                        fragrance_score: scores.fragrance_score || 0,
                        aroma_rating: scores.aroma_rating || undefined,
                        flavor_score: scores.flavor_score || 0,
                        aftertaste_score: scores.aftertaste_score || 0,
                        acidity_score: scores.acidity_score || 0,
                        sweetness_score: scores.sweetness_score || 0,
                        body_score: scores.body_score || 0,
                        overall_score: scores.overall_score || 0,
                        balance_score: scores.balance_score || undefined,
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Origin Info */}
            {sample && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    {t("coffees.section_origin", "Origin")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-2 text-sm">
                    {sample.origin && (
                      <div>
                        <dt className="text-muted-foreground text-xs">
                          {t("coffees.origin", "Origin")}
                        </dt>
                        <dd className="font-medium">{sample.origin}</dd>
                      </div>
                    )}
                    {sample.land && (
                      <div>
                        <dt className="text-muted-foreground text-xs">
                          {t("coffees.region", "Region")}
                        </dt>
                        <dd className="font-medium">{sample.land}</dd>
                      </div>
                    )}
                    {sample.producer && (
                      <div>
                        <dt className="text-muted-foreground text-xs">
                          {t("coffees.producer", "Producer")}
                        </dt>
                        <dd className="font-medium">{sample.producer}</dd>
                      </div>
                    )}
                    {sample.variety && (
                      <div>
                        <dt className="text-muted-foreground text-xs">
                          {t("coffees.variety", "Variety")}
                        </dt>
                        <dd className="font-medium">{sample.variety}</dd>
                      </div>
                    )}
                    {sample.process && (
                      <div>
                        <dt className="text-muted-foreground text-xs">
                          {t("coffees.process", "Process")}
                        </dt>
                        <dd className="font-medium">{sample.process}</dd>
                      </div>
                    )}
                  </dl>
                </CardContent>
              </Card>
            )}

            {/* Visibility Toggles */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("coffees.section_visibility", "Visibility")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(
                  Object.keys(visibilityToggles) as Array<
                    keyof VisibilityToggles
                  >
                ).map((key) => (
                  <div key={key} className="flex items-center justify-between">
                    <Label htmlFor={key} className="text-sm font-normal">
                      {t(
                        `coffees.toggle_${key}`,
                        key.replace("show_", "").replace(/_/g, " "),
                      )}
                    </Label>
                    <Switch
                      id={key}
                      checked={visibilityToggles[key]}
                      onCheckedChange={() => toggleVisibility(key)}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
